import { useParams } from 'react-router';
import { z } from 'zod';

import type { Id } from './common.ts';
import type { Workspace } from './generated/schemas.ts';
import { useListWorkspaces } from './generated/workspaces.ts';

// Lowercase letters, digits and single hyphens, e.g. 'studio-nord'. Globally unique; the API answers 409 otherwise.
// For the form, with a translated message; the API's own rule is in WorkspaceInput (generated/schemas.ts).
export const workspaceSlugSchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'validation.slug')
    .max(40, 'validation.slug');

// The workspace of the URL (/w/:workspaceSlug/…). AppLayout only renders its pages once the workspace is loaded and
// known, so this always finds it there.
export const useWorkspace = (): Workspace => {
    const { workspaceSlug } = useParams();
    const workspaces = useListWorkspaces();
    const workspace = workspaces.data?.find((item) => item.slug === workspaceSlug);
    if (!workspace) throw new Error(`useWorkspace is used outside of a loaded workspace (${workspaceSlug})`);
    return workspace;
};

// The API hooks scope their requests and caches to this id.
export const useWorkspaceId = (): Id => useWorkspace().id;

// App paths are written without the workspace prefix, e.g. getWorkspacePath('studio-nord', '/documents') →
// '/w/studio-nord/documents'.
export const getWorkspacePath = (slug: string, path = '/') => `/w/${slug}${path === '/' ? '' : path}`;

// The same within the workspace of the URL: workspacePath('/documents').
export const useWorkspacePath = () => {
    const { slug } = useWorkspace();
    return (path: string) => getWorkspacePath(slug, path);
};
